import { supabase } from "@/integrations/supabase/client";

let lastReconnectAt = 0;
const MIN_INTERVAL_MS = 3000; // debounce — avoid reconnect storms from rapid resume/network events

// Call-signaling critical section. While a WebRTC signaling channel is being
// joined or is live, external triggers (app-resume, the flapping Capacitor
// Network plugin, browser online events) must NOT hard-cycle the websocket:
// every disconnect()+connect() fans CHANNEL_ERROR ("transport failure") into
// the signaling join and killed call setup repeatedly (PostHog: "realtime
// reconnect looping (network)" concurrent with every failed call, Jul 13).
// The signaling services' own failure-recovery path bypasses this on purpose;
// nativeRealtimeLifecycle's gentle conditional connect() is unaffected, so a
// genuinely dead socket still recovers mid-call.
let suppressionHolders = 0;

export function beginForcedReconnectSuppression(reason: string): void {
  suppressionHolders++;
  console.info("Realtime: forced reconnects suppressed", { reason, holders: suppressionHolders });
}

export function endForcedReconnectSuppression(reason: string): void {
  suppressionHolders = Math.max(0, suppressionHolders - 1);
  console.info("Realtime: forced reconnect suppression released", { reason, holders: suppressionHolders });
}

/**
 * True while a WebRTC signaling channel is being joined or is live.
 *
 * Exposed so the idle-socket watcher (realtimeIdleSocket.ts) can honour the same
 * critical section: tearing the websocket down underneath a call being set up is
 * exactly the failure this suppression exists to prevent. Read-only — only
 * begin/endForcedReconnectSuppression may change it.
 */
export function isForcedReconnectSuppressed(): boolean {
  return suppressionHolders > 0;
}

/**
 * Force Supabase Realtime to re-establish its underlying websocket.
 *
 * Distinct from nativeRealtimeLifecycle's reconnectRealtime(), which only
 * calls connect() when phoenix reports NOT connected — useless against the
 * confirmed Android half-open-socket failure mode where isConnected() stays
 * true on a dead socket. This does an unconditional disconnect()+connect()
 * cycle, tearing the zombie socket down for real.
 *
 * Safe to call liberally — debounced, suppressed during active call
 * signaling, and disconnect()/connect() are idempotent no-ops if already in
 * the target state.
 */
export function forceRealtimeReconnect(
  reason: string,
  opts?: { bypassDebounce?: boolean; bypassSuppression?: boolean },
): boolean {
  const now = Date.now();
  if (!opts?.bypassSuppression && suppressionHolders > 0) {
    console.info("Realtime: forced reconnect skipped (call signaling active)", { reason });
    return false;
  }
  if (!opts?.bypassDebounce && now - lastReconnectAt < MIN_INTERVAL_MS) return false;
  lastReconnectAt = now;
  try {
    console.info("Realtime: forcing reconnect", { reason });
    supabase.realtime.disconnect();
    supabase.realtime.connect();
    return true;
  } catch (err) {
    console.warn("Realtime: forceRealtimeReconnect failed", err);
    return false;
  }
}
